import React, { useMemo, useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Add, Delete, Edit, Visibility } from "@mui/icons-material";
import { CacheKey } from "../../../types/cache-keys";
import { useDeleteItemMutation, validId } from "../../../hooks/base-hooks";
import GtmSolutionUseCaseEditorDialog from "../Editors/GtmSolutionUseCaseEditorDialog";
import ProductComponentEditorDialog from "../Editors/ProductComponentEditorDialog";
import ProductUseCaseEditorDialog from "../Editors/ProductUseCaseEditorDialog";
import ProductFeatureEditorDialog from "../Editors/ProductFeatureEditorDialog";
import { useDeleteGtmSolutionComponentMutation, useDeleteGtmSolutionUseCaseMutation, useFetchSingleGtmArchitecture } from "../../../hooks/gtm-solution-hooks";
import {
  useDeleteProductComponentMutation,
  useDeleteProductFeatureMutation,
  useDeleteProductUseCaseMutation,
  useFetchProductVersionFeatures,
  useFetchSingleProduct
} from "../../../hooks/product-hooks";
import GtmSolutionComponentEditorDialog from "../Editors/GtmSolutionComponentEditorDialog";
import ProductLicenseEditorDialog from '../Editors/ProductLicenseEditorDialog';
import ProductVersionEditorDialog from "../Editors/ProductVersionEditorDialog";
import ProductApplianceEditorDialog from "../Editors/ProductApplianceEditorDialog";
import { VersionScope } from "../../../types/snackableCourse";

export type ColumnConfig = {
  key: string;
  label: string;
};

type SectionConfig = Partial<{
  [K in CacheKey]: {
    title: string;
    columns: ColumnConfig[];
    renderCustomEditor?: (args: {
      numericId: number;
      open: boolean;
      row: any | null;
      onClose: () => void;
      onSaved: () => void;
      mode: "create" | "edit";
      readOnly: boolean;
      versionScope?: VersionScope | null;
    }) => React.ReactNode;
    rowProperty: string;
    deleteFn?: (...args: any[]) => ReturnType<typeof useDeleteItemMutation> | null;
  };
}>;

const sectionConfig: SectionConfig = {
  [CacheKey.GtmSolutionComponent]: {
    title: "Solution Components",
    columns: [
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly }) => (
      <GtmSolutionComponentEditorDialog
        open={open}
        mode={mode}
        gtmId={numericId}
        componentId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
      />
    ),
    rowProperty: "component",
    deleteFn: useDeleteGtmSolutionComponentMutation
  },
  [CacheKey.GtmSolutionUseCase]: {
    title: "Security Use Cases",
    columns: [
      { key: "order", label: "Order" },
      { key: "name", label: "Name" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly }) => (
      <GtmSolutionUseCaseEditorDialog
        open={open}
        mode={mode}
        gtmId={numericId}
        useCaseId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
      />
    ),
    rowProperty: "use_case",
    deleteFn: useDeleteGtmSolutionUseCaseMutation
  },
  [CacheKey.ProductComponent]: {
    title: "Product Components",
    columns: [
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly, versionScope }) => (
      <ProductComponentEditorDialog
        open={open}
        mode={mode}
        productId={numericId}
        componentId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
        versionScope={versionScope}
      />
    ),
    rowProperty: "component",
    deleteFn: useDeleteProductComponentMutation
  },
  [CacheKey.ProductUseCase]: {
    title: "Use Cases",
    columns: [
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly, versionScope }) => (
      <ProductUseCaseEditorDialog
        open={open}
        mode={mode}
        productId={numericId}
        useCaseId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
        versionScope={versionScope}
      />
    ),
    rowProperty: "use_case",
    deleteFn: useDeleteProductUseCaseMutation
  },
  [CacheKey.ProductFeature]: {
    title: "Features",
    columns: [
      { key: "name", label: "Name" },
      { key: "description", label: "Description" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly, versionScope }) => (
      <ProductFeatureEditorDialog
        open={open}
        mode={mode}
        productId={numericId}
        featureId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
        versionScope={versionScope}
      />
    ),
    rowProperty: "feature",
    deleteFn: useDeleteProductFeatureMutation
  },
  [CacheKey.ProductVersion]: {
    title: "Versions",
    columns: [
      { key: "version", label: "Version" },
      { key: "released", label: "Released" },
      { key: "end_of_life", label: "End of Life" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly }) => (
      <ProductVersionEditorDialog
        open={open}
        mode={mode}
        productId={numericId}
        versionId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
        versionScope={
          row?.id
            ? {
              product_id: numericId,
              product_version_id: row.id,
              doc_version: row.version,
              deployment_scope: "appliance",
              generation_scope: "version",
              version_source: "selected_product_version",
            }
            : {
              product_id: numericId,
              generation_scope: "version",
            }
        }
      />
    ),
    rowProperty: "version",
  },
  [CacheKey.ProductLicense]: {
    title: "Licenses",
    columns: [
      { key: "name", label: "Name" },
      { key: "consumption_model", label: "Consumption Model" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly }) => (
      <ProductLicenseEditorDialog
        open={open}
        mode={mode}
        productId={numericId}
        licenseId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
      />
    ),
    rowProperty: "license",
  },
  [CacheKey.ProductAppliance]: {
    title: "Appliances",
    columns: [
      { key: "name", label: "Name" },
      { key: "deployment_type", label: "Deployment Type" },
      { key: "part_number", label: "Part Number" },
    ],
    renderCustomEditor: ({ numericId, open, mode, row, onClose, onSaved, readOnly }) => (
      <ProductApplianceEditorDialog
        open={open}
        mode={mode}
        productId={numericId}
        applianceId={row?.id}
        onClose={onClose}
        onSaved={onSaved}
        readOnly={readOnly}
      />
    ),
    rowProperty: "appliance",
  },
};

type ChildCrudPanelProps = {
  parentId: number | null;
  sectionType: CacheKey;
  readOnly?: boolean;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;

  /**
   * Optional formatting overrides.
   */
  titleOverride?: string;
  columnsOverride?: ColumnConfig[];

  /**
   * Product version-scoped catalog view.
   *
   * selectedVersionId:
   * - number => filter rows to that version
   * - null   => View All Versions / no version filter
   */
  versionScoped?: boolean;
  selectedVersionId?: number | null;
  selectedVersionLabel?: string;
  versionScope?: VersionScope | null;
};

const VERSION_FILTERABLE_SECTIONS = new Set<CacheKey>([
  CacheKey.ProductFeature,
  CacheKey.ProductComponent,
  CacheKey.ProductUseCase,
]);

const toId = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;

  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  if (typeof value === "object" && value !== null && "id" in value) {
    return toId((value as { id?: unknown }).id);
  }

  return null;
};

const getFeatureIdsFromRow = (row: any): number[] => {
  const possibleArrays = [
    row?.product_feature,
    row?.product_features,
    row?.feature,
    row?.features,
  ];

  const values = possibleArrays.find((value) => Array.isArray(value)) ?? [];

  return Array.from(
    new Set(
      values
        .map((value: any) => toId(value))
        .filter((id: number | null): id is number => id != null)
    )
  );
};

const getFeatureIdFromVersionJoin = (join: any): number | null => {
  return (
    toId(join?.product_feature) ||
    toId(join?.feature) ||
    null
  );
};

export default function ChildCrudPanel({
                                         parentId,
                                         sectionType,
                                         readOnly = false,
                                         canCreate = true,
                                         canUpdate = true,
                                         canDelete = true,
                                         titleOverride,
                                         columnsOverride,
                                         versionScoped = false,
                                         selectedVersionId = null,
                                         selectedVersionLabel = "",
                                         versionScope = null,
                                       }: ChildCrudPanelProps) {
  const config = sectionConfig[sectionType];

  const {
    title,
    columns,
    renderCustomEditor,
    deleteFn = null,
  } = config;

  const resolvedColumns = columnsOverride?.length ? columnsOverride : columns;
  const resolvedBaseTitle = titleOverride || title;

  const invalidParentId = !validId(parentId);
  const isGtmSection =
    sectionType === CacheKey.GtmSolutionComponent ||
    sectionType === CacheKey.GtmSolutionUseCase;

  const isProductSection =
    sectionType === CacheKey.ProductComponent ||
    sectionType === CacheKey.ProductFeature ||
    sectionType === CacheKey.ProductUseCase ||
    sectionType === CacheKey.ProductVersion ||
    sectionType === CacheKey.ProductLicense ||
    sectionType === CacheKey.ProductAppliance;

  const versionViewApplies =
    versionScoped &&
    VERSION_FILTERABLE_SECTIONS.has(sectionType);

  const versionFilterApplies =
    versionViewApplies &&
    validId(selectedVersionId);

  const isViewAllVersions =
    versionViewApplies &&
    !validId(selectedVersionId);

  const { data: architecture, isLoading: loadingArchitecture } = useFetchSingleGtmArchitecture(parentId, {}, { enabled: isGtmSection });
  const { data: product, isLoading: loadingProduct } = useFetchSingleProduct(parentId, {}, { enabled: isProductSection });

  const {
    data: versionFeatureLinks = [],
    isLoading: loadingVersionFeatures,
  } = useFetchProductVersionFeatures(
    selectedVersionId,
    { detailed: true, size: 2000 },
    {
      enabled: versionFilterApplies,
    }
  );

  const parentData = isGtmSection
    ? architecture
    : isProductSection
      ? product
      : null;

  const parentLoading = isGtmSection
    ? loadingArchitecture
    : isProductSection
      ? loadingProduct
      : false;

  const versionFeatureIds = useMemo(() => {
    return new Set(
      (versionFeatureLinks ?? [])
        .map((link: any) => getFeatureIdFromVersionJoin(link))
        .filter((id: number | null): id is number => id != null)
    );
  }, [versionFeatureLinks]);

  const rows = useMemo(() => {
    if (parentLoading) return [];
    if (!parentData) return [];

    const prop = sectionConfig[sectionType].rowProperty;
    const items = parentData?.[prop] ?? [];
    const baseRows = Array.isArray(items) ? items : [];

    if (!versionFilterApplies) {
      return baseRows;
    }

    if (sectionType === CacheKey.ProductFeature) {
      return baseRows.filter((row: any) => versionFeatureIds.has(Number(row.id)));
    }

    if (
      sectionType === CacheKey.ProductComponent ||
      sectionType === CacheKey.ProductUseCase
    ) {
      return baseRows.filter((row: any) => {
        const rowFeatureIds = getFeatureIdsFromRow(row);
        return rowFeatureIds.some((featureId) => versionFeatureIds.has(featureId));
      });
    }

    return baseRows;
  }, [
    parentData,
    parentLoading,
    sectionType,
    versionFilterApplies,
    versionFeatureIds,
  ]);

  const { mutateAsync: deleteMutation } = deleteFn && !invalidParentId
    ? deleteFn(parentId)
    : { mutateAsync: async () => {} };

  const handleDeleteMutation = async ({ id }: { id: number }) => {
    if (window.confirm("Are you sure you want to delete this item? This action cannot be undone.")) {
      await deleteMutation({ id });
    }
  };

  const [customOpen, setCustomOpen] = useState(false);
  const [customMode, setCustomMode] = useState<'create' | 'edit'>('create');
  const [customRow, setCustomRow] = useState<any | null>(null);

  const effectiveCanCreate = !readOnly && canCreate && renderCustomEditor !== undefined;
  const effectiveCanUpdate = !readOnly && canUpdate && renderCustomEditor !== undefined;
  const effectiveCanDelete = !readOnly && canDelete;

  const effectiveLoading = parentLoading || (versionFilterApplies && loadingVersionFeatures);

  const effectiveTitle =
    versionFilterApplies && selectedVersionLabel
      ? `${resolvedBaseTitle} for ${selectedVersionLabel}`
      : isViewAllVersions
        ? `${resolvedBaseTitle} across all versions`
        : resolvedBaseTitle;

  const helperText =
    versionFilterApplies
      ? "Showing only records related to features available in the selected product version."
      : isViewAllVersions
        ? "View All Versions is selected. Showing all records so they can be mapped to specific versions where applicable."
        : "";

  const emptyMessage =
    versionFilterApplies && selectedVersionId
      ? `None available for ${selectedVersionLabel || "the selected version"}.`
      : isViewAllVersions
        ? "None created across all versions."
        : "None created.";

  const openAdd = () => {
    if (invalidParentId || !effectiveCanCreate) return;
    setCustomMode("create");
    setCustomRow(null);
    setCustomOpen(true);
  };

  const openEditOrView = (row: any) => {
    if (invalidParentId) return;
    setCustomMode('edit');
    setCustomRow(row);
    setCustomOpen(true);
  };

  const closeCustom = () => {
    setCustomOpen(false);
    setCustomRow(null);
  };

  const onSaved = () => {
    closeCustom();
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Typography variant="h6">{effectiveTitle}</Typography>
          {!!helperText && (
            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={2}>
          {effectiveLoading && <CircularProgress size={18} />}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={openAdd}
            disabled={invalidParentId || !effectiveCanCreate}
          >
            Add
          </Button>
        </Box>
      </Box>

      {invalidParentId && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Save the parent record first to manage items.
        </Typography>
      )}

      <TableContainer sx={{ maxHeight: 320, mt: 2 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {resolvedColumns.map((c) => (
                <TableCell key={c.key}>{c.label}</TableCell>
              ))}
              <TableCell width={120}>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((r: any) => (
              <TableRow key={r.id} hover>
                {resolvedColumns.map((c) => (
                  <TableCell key={c.key}>{r?.[c.key]}</TableCell>
                ))}

                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  {effectiveCanUpdate
                    ? (
                      <Tooltip title="Edit">
                        <span>
                          <IconButton size="small" onClick={() => openEditOrView(r)} disabled={invalidParentId}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )
                    : renderCustomEditor && (
                    <Tooltip title="View">
                        <span>
                          <IconButton size="small" onClick={() => openEditOrView(r)} disabled={invalidParentId}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </span>
                    </Tooltip>
                  )
                  }

                  {effectiveCanDelete && deleteFn !== null && (
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteMutation({ id: r.id })}
                          disabled={invalidParentId}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}

            {!effectiveLoading && rows?.length === 0 && (
              <TableRow>
                <TableCell colSpan={resolvedColumns.length + 1}>
                  <Typography variant="body2" color="text.secondary">
                    {emptyMessage}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {renderCustomEditor &&
        renderCustomEditor({
          numericId: parentId,
          open: customOpen,
          mode: customMode,
          row: customRow,
          onClose: closeCustom,
          onSaved,
          readOnly: readOnly || !canUpdate,
          versionScope,
        })}
    </Paper>
  );
}